import { z } from "zod";

export const planEnum = z.enum(["free", "pro", "enterprise"]);
export type Plan = z.infer<typeof planEnum>;

export const sourceTypeEnum = z.enum(["document", "web", "manual"]);
export type SourceType = z.infer<typeof sourceTypeEnum>;

export const sourceCategoryEnum = z.enum(["design_system"]);
export type SourceCategory = z.infer<typeof sourceCategoryEnum>;

export const authorityLevelEnum = z.enum([
  "legislation",
  "regulation",
  "standard",
  "company_policy",
  "guideline",
  "informational",
]);
export type AuthorityLevel = z.infer<typeof authorityLevelEnum>;

export const sourceStatusEnum = z.enum([
  "pending",
  "processing",
  "ready",
  "ready_unverified",
  "error",
  "stale",
]);
export type SourceStatus = z.infer<typeof sourceStatusEnum>;

export const chunkTypeEnum = z.enum(["child", "parent"]);
export type ChunkType = z.infer<typeof chunkTypeEnum>;

export const referenceTypeEnum = z.enum([
  "implements",
  "references",
  "supersedes",
  "amends",
  "conflicts_with",
]);
export type ReferenceType = z.infer<typeof referenceTypeEnum>;

// Experience-layer enums — mirror packages/db/src/schema.ts
// NOTE: compliance/taskImpact/confidence/taskCriticality/fixStatus already live
// in `validation/finding-metrics.ts`. Import them from there to avoid duplicate
// type exports.

export const findingTypeEnum = z.enum([
  "helps",
  "hinders",
  "missing",
  "irrelevant",
  "incorrect",
]);
export type FindingType = z.infer<typeof findingTypeEnum>;

/**
 * Harm to the person, one scale for both sources. A strength (`helps`)
 * carries `nit`. Mirrors `finding_severity` in packages/db.
 */
export const findingSeverityEnum = z.enum([
  "critical",
  "major",
  "minor",
  "nit",
]);
export type FindingSeverity = z.infer<typeof findingSeverityEnum>;

/** The level `nit` replaced. Read from old CLIs, runners and stored rows. */
export const LEGACY_SEVERITY = "observation";

/** Maps the legacy `observation` level to `nit`; other values pass through. */
export function normalizeSeverity<T>(value: T): T | "nit" {
  return value === LEGACY_SEVERITY ? "nit" : value;
}

/** Severity as read from a file or a wire payload: `observation` reads as `nit`. */
export const findingSeverityField = z.preprocess(
  normalizeSeverity,
  findingSeverityEnum,
);

/** Who found a finding: the persona walking, or the designer looking afterwards. */
export const FINDING_SOURCES = ["persona", "designer"] as const;
export const findingSourceEnum = z.enum(FINDING_SOURCES);
export type FindingSource = z.infer<typeof findingSourceEnum>;

/**
 * How strong a designer claim is: `evidence` (a persona finding shows
 * it), `seen` (seen at a recorded step) or `taste`. Older CLIs say
 * `observed` for `seen`.
 */
export const DESIGNER_BASES = ["evidence", "seen", "taste"] as const;
export const designerBasisEnum = z.enum(DESIGNER_BASES);
export type DesignerBasis = z.infer<typeof designerBasisEnum>;
export const designerBasisField = z.preprocess(
  (v) => (v === "observed" ? "seen" : v),
  designerBasisEnum,
);

/** Whether the designer's review was written by a separate agent (`inline`: the orchestrator). */
export const designerDispatchEnum = z.enum(["subagent", "inline"]);
export type DesignerDispatch = z.infer<typeof designerDispatchEnum>;

/** Who found it: `source`, else the legacy `lens: designer`, else the persona. */
export function findingSource(fm: {
  source?: string | null;
  lens?: string | null;
}): FindingSource {
  if (fm.source === "designer" || fm.source === "persona") return fm.source;
  return fm.lens === "designer" ? "designer" : "persona";
}

export const regulatoryWeightEnum = z.enum([
  "hard_requirement",
  "process_model",
  "best_practice",
]);
export type RegulatoryWeight = z.infer<typeof regulatoryWeightEnum>;

export const personaStatusEnum = z.enum(["active", "archived"]);
export type PersonaStatus = z.infer<typeof personaStatusEnum>;

export const scenarioStatusEnum = z.enum([
  "draft",
  "sources_approved",
  "ready",
  "tests_generated",
  "tests_approved",
  "rules_generating",
  "rules_ready",
  "spec_compiling",
  "spec_draft",
  "spec_published",
]);
export type ScenarioStatus = z.infer<typeof scenarioStatusEnum>;

export const scenarioValidationStatusEnum = z.enum([
  "passing",
  "failing",
  "conflicts",
  "stale",
]);
export type ScenarioValidationStatus = z.infer<
  typeof scenarioValidationStatusEnum
>;

export const triageStatusEnum = z.enum([
  "open",
  "addressed",
  "deferred",
  "wont_fix",
  "dismissed",
]);
export type TriageStatus = z.infer<typeof triageStatusEnum>;
